import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Building2, ArrowLeft, ArrowRight, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import Logo from "../../components/shared/Logo";
import { tenantAPI } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const WorkspaceSelectPage = () => {
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { register } = useAuth();

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      const response = await tenantAPI.listPublic();
      setTenants(response.data);
    } catch (error) {
      console.error("Failed to fetch tenants:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTenants = tenants.filter((tenant) =>
    tenant.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleContinue = async () => {
    const signupData = JSON.parse(sessionStorage.getItem("signup_data") || "{}");
    
    if (!signupData.email || !signupData.password || !signupData.fullName) {
      toast.error("Missing registration data", { description: "Please start again from signup" });
      navigate("/auth/signup");
      return;
    }

    setSubmitting(true);

    try {
      const user = await register(
        signupData.email,
        signupData.password,
        signupData.fullName,
        signupData.role || "learner",
        selectedTenant
      );

      sessionStorage.removeItem("signup_data");
      toast.success("Account created!", { description: `Welcome, ${user.full_name}!` });

      // Redirect based on role
      if (user.role === "super_admin") {
        navigate("/admin/dashboard");
      } else if (user.role === "tenant_admin") {
        navigate("/tenant/dashboard");
      } else {
        navigate("/learner/dashboard");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to create account";
      toast.error("Registration failed", { description: message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center p-4">
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl floating" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl floating-delayed" />
      </div>

      <div className="w-full max-w-2xl relative z-10 animate-fade-in">
        {/* Back Button */}
        <Link
          to="/auth/role"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to role selection
        </Link>

        <div className="glass-card p-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <Logo size="lg" />
          </div>

          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-foreground mb-2" style={{ fontFamily: "'Sora', sans-serif" }}>
              Select your workspace
            </h1>
            <p className="text-muted-foreground">Choose an organization or continue without one</p>
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search organizations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-11 bg-background/50 border-border/50"
              data-testid="workspace-search-input"
            />
          </div>

          {/* Tenant List */}
          <div className="space-y-3 mb-6 max-h-64 overflow-y-auto">
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">
                Loading workspaces...
              </div>
            ) : filteredTenants.length > 0 ? (
              filteredTenants.map((tenant) => (
                <button
                  key={tenant.id}
                  onClick={() => setSelectedTenant(tenant.id)}
                  className={`w-full p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                    selectedTenant === tenant.id
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-muted-foreground/30 bg-background/50"
                  }`}
                  data-testid={`workspace-option-${tenant.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                      {tenant.logo_url ? (
                        <img src={tenant.logo_url} alt={tenant.name} className="w-full h-full rounded-lg object-cover" />
                      ) : (
                        <Building2 className="w-5 h-5 text-accent" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-foreground truncate">{tenant.name}</h3>
                      <p className="text-xs text-muted-foreground">{tenant.subdomain}.odesie.com</p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                      selectedTenant === tenant.id ? "border-primary" : "border-border"
                    }`}>
                      {selectedTenant === tenant.id && (
                        <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                      )}
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                {searchQuery ? "No matching organizations found" : "No organizations available"}
              </div>
            )}
          </div>

          {/* Skip Option */}
          <button
            onClick={() => setSelectedTenant(null)}
            className={`w-full p-4 rounded-xl border-2 border-dashed transition-all duration-200 text-left mb-6 ${
              selectedTenant === null
                ? "border-primary bg-primary/5"
                : "border-border hover:border-muted-foreground/30 bg-background/30"
            }`}
            data-testid="workspace-skip-option"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                <Plus className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-foreground">Continue without organization</h3>
                <p className="text-xs text-muted-foreground">Join an organization later</p>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                selectedTenant === null ? "border-primary" : "border-border"
              }`}>
                {selectedTenant === null && (
                  <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                )}
              </div>
            </div>
          </button>

          {/* Continue Button */}
          <Button 
            onClick={handleContinue}
            disabled={submitting}
            className="w-full h-12 text-base font-semibold btn-primary"
            data-testid="workspace-continue-btn"
          >
            {submitting ? "Creating account..." : "Complete Signup"}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>

          {/* Login Link */}
          <p className="text-center mt-6 text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/auth/login" className="text-primary font-medium hover:text-primary/80 transition-colors">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default WorkspaceSelectPage;
